/**
 * CuentaNode — nodo recursivo del árbol del catálogo.
 *
 * El estado `expanded` ya NO vive aquí — vive en ListCatalogo
 * en un Set<id> que sobrevive al refetch. Esto evita que los acordeones
 * se colapsen al agregar/editar una cuenta.
 *
 * Props:
 *   cuenta        → objeto CuentaContable con subcuentas anidadas
 *   depth         → nivel de indentación visual (0-based)
 *   expandedIds   → Set<number> de IDs actualmente expandidos
 *   onToggle      → fn(id) → toggle en el Set del padre
 *   onAgregar     → fn(cuenta)
 *   onEditar      → fn(cuenta)
 *   onDesactivar  → fn(id, nombre)
 *   onActivar     → fn(id)
 */

const TIPO_COLOR = {
  ACTIVO:     { text: "#60a5fa" },
  PASIVO:     { text: "#f87171" },
  PATRIMONIO: { text: "#a78bfa" },
  INGRESO:    { text: "#4ade80" },
  GASTO:      { text: "#fbbf24" },
};

export function CuentaNode({
  cuenta,
  depth = 0,
  expandedIds,
  onToggle,
  onAgregar,
  onEditar,
  onDesactivar,
  onActivar,
}) {
  const expanded   = expandedIds.has(cuenta.id);
  const tieneHijos = cuenta.subcuentas?.length > 0 || cuenta.tiene_hijos;
  const color      = TIPO_COLOR[cuenta.tipo] || TIPO_COLOR.ACTIVO;
  const indent     = depth * 20;

  // Colores del sistema real
  const bgRow      = depth === 0 ? "rgba(55,65,81,0.15)" : "transparent";
  const bgRowHover = "rgba(55,65,81,0.4)";

  return (
    <div>
      <div
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          8,
          padding:      `7px 14px 7px ${14 + indent}px`,
          borderBottom: "1px solid #374151",
          background:   bgRow,
          opacity:      cuenta.activa ? 1 : 0.5,
          transition:   "background 0.12s",
          cursor:       "default",
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = bgRowHover; }}
        onMouseOut={(e)  => { e.currentTarget.style.background = bgRow; }}
      >
        {/* Toggle */}
        <button
          type="button"
          onClick={() => onToggle(cuenta.id)}
          disabled={!tieneHijos}
          style={{
            background: "none", border: "none",
            cursor: tieneHijos ? "pointer" : "default",
            color: tieneHijos ? "#f97316" : "transparent",
            width: 18, flexShrink: 0, fontSize: "0.68rem", padding: 0,
          }}
        >
          <i className={`fa-solid fa-chevron-${expanded ? "down" : "right"}`} />
        </button>

        {/* Dot indicador */}
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: color.text, flexShrink: 0,
          opacity: cuenta.is_detalle ? 1 : 0.4,
        }} />

        {/* Código */}
        <span style={{
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: "0.76rem", color: "#6b7280",
          minWidth: 112, flexShrink: 0,
          textDecoration: cuenta.activa ? "none" : "line-through",
        }}>
          {cuenta.codigo}
        </span>

        {/* Nombre */}
        <span style={{
          flex: 1,
          color: depth === 0 ? "#f9fafb" : "#9ca3af",
          fontSize: depth === 0 ? "0.87rem" : "0.83rem",
          fontWeight: depth === 0 ? 600 : 400,
        }}>
          {cuenta.nombre}
        </span>

        {/* Badges */}
        <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
          <span style={{
            fontSize: "0.63rem", fontWeight: 700,
            padding: "2px 7px", borderRadius: 20,
            background: `${color.text}18`,
            border: `1px solid ${color.text}40`,
            color: color.text,
          }}>
            {cuenta.tipo}
          </span>

          {cuenta.is_detalle && (
            <span style={{
              fontSize: "0.63rem", fontWeight: 700,
              padding: "2px 7px", borderRadius: 20,
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.35)",
              color: "#f97316",
            }}>
              DETALLE
            </span>
          )}

          {!cuenta.activa && (
            <span style={{
              fontSize: "0.63rem", padding: "2px 7px", borderRadius: 20,
              background: "rgba(107,114,128,0.15)", color: "#6b7280",
              border: "1px solid rgba(107,114,128,0.3)",
            }}>
              INACTIVA
            </span>
          )}

          <span style={{ fontSize: "0.63rem", color: "#374151", minWidth: 12, textAlign: "center" }}>
            {cuenta.naturaleza === "DEUDORA" ? "D" : "H"}
          </span>
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {!cuenta.is_detalle && cuenta.nivel < 4 && cuenta.activa && (
            <ActionBtn icon="fa-plus"     title="Agregar subcuenta" hoverColor="#f97316" onClick={() => onAgregar(cuenta)} />
          )}
          {cuenta.nivel > 1 && (
            <ActionBtn icon="fa-pen"      title="Editar"            hoverColor="#60a5fa" onClick={() => onEditar(cuenta)} />
          )}
          {cuenta.nivel > 1 && (
            cuenta.activa
              ? <ActionBtn icon="fa-eye-slash" title="Desactivar" hoverColor="#f87171" onClick={() => onDesactivar(cuenta.id, cuenta.nombre)} />
              : <ActionBtn icon="fa-eye"       title="Reactivar"  hoverColor="#4ade80" onClick={() => onActivar(cuenta.id)} />
          )}
        </div>
      </div>

      {/* Subcuentas recursivas */}
      {expanded && cuenta.subcuentas?.map((sub) => (
        <CuentaNode
          key={sub.id}
          cuenta={sub}
          depth={depth + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onAgregar={onAgregar}
          onEditar={onEditar}
          onDesactivar={onDesactivar}
          onActivar={onActivar}
        />
      ))}
    </div>
  );
}

function ActionBtn({ icon, title, hoverColor, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        background: "none", border: "none", cursor: "pointer",
        color: "#6b7280", padding: "3px 5px", borderRadius: 5,
        fontSize: "0.7rem", transition: "color 0.15s, background 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.color = hoverColor;
        e.currentTarget.style.background = `${hoverColor}18`;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.color = "#6b7280";
        e.currentTarget.style.background = "none";
      }}
    >
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}
